import { Button, Grid, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import * as React from "react";

import { LoadingWrapper } from "../../../components/LoadingWrapper";
import { Helpers } from "../../../utils/helpers";
import { fetchTournamentList, TournamentBattleLogListItem, TournamentListResponse } from "../api";
import { formatTournamentDuration, renderTournamentStatus, TournamentEmptyState, TournamentPageHeader, TournamentSourceNotice } from "../components";

const initialFilters = {
    query: "",
    tournamentId: "",
    round: "",
    player: "",
    status: "",
    layoutId: "",
    environmentId: "",
    page: 1,
    pageSize: 10,
};

export const TournamentBattleLogs = (props: any) => {
    const [filters, setFilters] = React.useState(initialFilters);
    const [response, setResponse] = React.useState<TournamentListResponse<TournamentBattleLogListItem> | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        document.title = Helpers.documentTitle("Tournament Battle Logs");
        loadList(initialFilters);
    }, []);

    const loadList = (nextFilters = filters) => {
        setLoading(true);
        setError(null);

        fetchTournamentList<TournamentBattleLogListItem>("/api/tournaments/battle-logs", nextFilters, props.activeEnv)
            .then(data => {
                setResponse(data);
                setFilters(nextFilters);
            })
            .catch(err => {
                setError(err?.response?.data?.message || err?.message || "Couldn't load tournament battle logs.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleFilterChange = (key: string) => (event) => {
        setFilters({
            ...filters,
            [key]: event.target.value,
        });
    };

    const handleSearch = () => {
        loadList({
            ...filters,
            page: 1,
        });
    };

    const handleClear = () => {
        loadList(initialFilters);
    };

    const handlePageChange = (_event: unknown, page: number) => {
        loadList({
            ...filters,
            page: page + 1,
        });
    };

    const handleRowsPerPageChange = (event) => {
        loadList({
            ...filters,
            page: 1,
            pageSize: parseInt(event.target.value, 10),
        });
    };

    const items = response?.data.items || [];
    const page = (response?.data.page || 1) - 1;
    const pageSize = response?.data.pageSize || filters.pageSize;
    const total = response?.data.total || 0;

    return <div className="contentPadding">
        <TournamentPageHeader
            title="Tournament / Battle Logs"
        />

        <Paper className="root" elevation={0} style={{ marginTop: 16 }}>
            <div style={{ float: "left", width: "100%", padding: "22px 22px 0 22px" }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            label="Search"
                            value={filters.query}
                            onChange={handleFilterChange("query")}
                            placeholder="Battle ID, tournament, player"
                        />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth label="Tournament ID" value={filters.tournamentId} onChange={handleFilterChange("tournamentId")} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <TextField fullWidth label="Round" value={filters.round} onChange={handleFilterChange("round")} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth label="Player" value={filters.player} onChange={handleFilterChange("player")} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <TextField
                            fullWidth
                            select
                            label="Status"
                            value={filters.status}
                            onChange={handleFilterChange("status")}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="running">Running</MenuItem>
                            <MenuItem value="finished">Finished</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={1}>
                        <TextField fullWidth label="Layout ID" value={filters.layoutId} onChange={handleFilterChange("layoutId")} />
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth label="Environment ID" value={filters.environmentId} onChange={handleFilterChange("environmentId")} />
                    </Grid>
                </Grid>

                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <Button disableElevation variant="contained" color="primary" onClick={handleSearch}>
                        Search
                    </Button>
                    <Button disableElevation variant="contained" color="secondary" onClick={handleClear} style={{ marginLeft: 8 }}>
                        Clear
                    </Button>
                </div>
            </div>

            <div style={{ float: "left", width: "100%", padding: "0 22px 22px 22px" }}>
                <TournamentSourceNotice meta={response?.meta} />

                <LoadingWrapper loading={loading} error={error}>
                    {items.length === 0 ? <TournamentEmptyState
                        title="No tournament battle logs found"
                        description="Try a broader filter set or wire the adapter to the authoritative tournament source."
                    /> : <React.Fragment>
                        <Table className="min-table-size">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Battle ID</TableCell>
                                    <TableCell>Tournament</TableCell>
                                    <TableCell>Round</TableCell>
                                    <TableCell>Layout</TableCell>
                                    <TableCell>Environment</TableCell>
                                    <TableCell>Player A</TableCell>
                                    <TableCell>Player B</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>Created at</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {items.map(item => <TableRow hover key={item.id}>
                                    <TableCell>
                                        <Button size="small" color="primary" onClick={() => props.history.push(`/tournament/battle-logs/${item.id}`)}>
                                            {item.id}
                                        </Button>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.tournamentName || "Unknown"}</Typography>
                                        <Typography variant="caption" color="textSecondary">{item.tournamentId || "n/a"}</Typography>
                                    </TableCell>
                                    <TableCell>{item.round || "n/a"}</TableCell>
                                    <TableCell>{item.layoutId || "n/a"}</TableCell>
                                    <TableCell>{item.environmentId || "n/a"}</TableCell>
                                    <TableCell>{item.playerA || "n/a"}</TableCell>
                                    <TableCell>{item.playerB || "n/a"}</TableCell>
                                    <TableCell>{renderTournamentStatus(item.status)}</TableCell>
                                    <TableCell>{formatTournamentDuration(item.durationSeconds)}</TableCell>
                                    <TableCell>{item.createdAt ? Helpers.formatDateWithTime(item.createdAt, true) : "n/a"}</TableCell>
                                    <TableCell>
                                        <Button size="small" variant="outlined" onClick={() => props.history.push(`/tournament/battle-logs/${item.id}`)}>
                                            View
                                        </Button>
                                    </TableCell>
                                </TableRow>)}
                            </TableBody>
                        </Table>

                        <TablePagination
                            component="div"
                            count={total}
                            page={page}
                            rowsPerPage={pageSize}
                            onPageChange={handlePageChange}
                            onRowsPerPageChange={handleRowsPerPageChange}
                            rowsPerPageOptions={[5, 10, 20]}
                        />
                    </React.Fragment>}
                </LoadingWrapper>
            </div>
        </Paper>
    </div>;
};
